import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, User, Bot, Minimize2, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Generate a unique session ID
const getSessionId = () => {
  let sessionId = localStorage.getItem('slotfleet_chat_session');
  if (!sessionId) {
    sessionId = 'chat_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('slotfleet_chat_session', sessionId);
  }
  return sessionId;
};

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasGreeted, setHasGreeted] = useState(false);
  const [collectedInfo, setCollectedInfo] = useState({});
  const [viewportHeight, setViewportHeight] = useState(typeof window !== 'undefined' ? `${window.innerHeight}px` : '100vh');
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 640 : false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const chatContainerRef = useRef(null);
  const sessionId = useRef(getSessionId());

  // Handle visual viewport changes (keyboard open/close)
  useEffect(() => {
    const updateDimensions = () => {
      setIsMobile(window.innerWidth < 640);
      if (window.visualViewport) {
        setViewportHeight(`${window.visualViewport.height}px`);
      } else {
        setViewportHeight(`${window.innerHeight}px`);
      }
    };

    if (typeof window !== 'undefined') {
      updateDimensions();
      
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', updateDimensions);
        window.visualViewport.addEventListener('scroll', updateDimensions);
      }
      window.addEventListener('resize', updateDimensions);

      return () => {
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', updateDimensions);
          window.visualViewport.removeEventListener('scroll', updateDimensions);
        }
        window.removeEventListener('resize', updateDimensions);
      };
    }
  }, [isOpen]);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load existing chat history
  useEffect(() => {
    const loadChatHistory = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/chatbot/session/${sessionId.current}`);
        if (res.ok) {
          const data = await res.json();
          if (data.chat_history && data.chat_history.length > 0) {
            setMessages(data.chat_history.map(msg => ({
              role: msg.role,
              content: msg.content
            })));
            setHasGreeted(true);
          }
          if (data.visitor_info) {
            setCollectedInfo(data.visitor_info);
          }
        }
      } catch (error) {
      }
    };
    loadChatHistory();
  }, []);

  // Auto-open chat after delay (only if user hasn't closed it this session)
  useEffect(() => {
    // Check if user has closed the chat in this session
    const hasClosedChat = sessionStorage.getItem('slotfleet_chat_closed');
    if (hasClosedChat) return;

    const timer = setTimeout(() => {
      if (!isOpen && isMinimized) {
        setIsMinimized(false);
        setIsOpen(true);
        
        // Send initial greeting if no history
        if (!hasGreeted && messages.length === 0) {
          setMessages([{
            role: 'assistant',
            content: "Hi there! I'm Laura, your guide to SlotFleet. I can help you learn about our skill games management platform, pricing, and features. What brings you here today?"
          }]);
          setHasGreeted(true);
        }
      }
    }, 5000); // 5 seconds delay

    return () => clearTimeout(timer);
  }, [isOpen, isMinimized, hasGreeted, messages.length]);

  const handleOpen = () => {
    setIsMinimized(false);
    setIsOpen(true);
    // Clear the closed flag when user manually opens
    sessionStorage.removeItem('slotfleet_chat_closed');
    
    if (!hasGreeted && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: "Hi there! I'm Laura, your guide to SlotFleet. I can help you learn about our skill games management platform, pricing, and features. What brings you here today?"
      }]);
      setHasGreeted(true);
    }
    
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleClose = async () => {
    setIsOpen(false);
    setIsMinimized(true);
    // Remember that user closed the chat (until page refresh)
    sessionStorage.setItem('slotfleet_chat_closed', 'true');
    
    // Send chat summary email when closing (if they provided email)
    if (collectedInfo.email && messages.length > 2) {
      try {
        await fetch(`${API_BASE}/api/chatbot/end-chat/${sessionId.current}`, {
          method: 'POST'
        });
      } catch (error) {
      }
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/chatbot/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId.current,
          message: userMessage,
          visitor_info: collectedInfo
        })
      });

      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
        if (data.collected_info) {
          setCollectedInfo(data.collected_info);
        }
      } else {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: "I apologize, but I'm having trouble connecting. Please try again or reach us at sales@slotfleet.com" 
        }]);
      }
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I apologize, but I'm having trouble connecting. Please try again or reach us at sales@slotfleet.com" 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Quick action buttons
  const quickActions = [
    { label: 'Pricing', message: 'What are your pricing plans?' },
    { label: 'Features', message: 'What features does SlotFleet offer?' },
    { label: 'Free Trial', message: 'How do I start a free trial?' },
  ];

  const handleQuickAction = (message) => {
    setInputValue(message);
    setTimeout(() => handleSendMessage(), 100);
  };

  return (
    <>
      {/* Chat bubble button */}
      <AnimatePresence>
        {isMinimized && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            onClick={handleOpen}
            className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-14 h-14 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full shadow-lg flex items-center justify-center hover:from-amber-400 hover:to-yellow-400 transition-all hover:scale-110"
            data-testid="chat-bubble"
          >
            <MessageCircle className="w-6 h-6 text-black" />
            {/* Notification dot */}
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
              <span className="text-[10px] text-white font-bold">1</span>
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={chatContainerRef}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed right-4 z-50 w-[calc(100%-2rem)] sm:w-[380px] max-w-[380px] h-[450px] sm:h-[500px] max-h-[70vh] bg-neutral-900 rounded-2xl shadow-2xl border border-amber-500/20 flex flex-col overflow-hidden"
            style={{
              bottom: isMobile && viewportHeight !== `${window.innerHeight}px` 
                ? `calc(${window.innerHeight}px - ${viewportHeight} + 16px)` 
                : '16px'
            }}
            data-testid="chat-window"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-500 to-yellow-500 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-black/20 rounded-full flex items-center justify-center">
                  <Bot className="w-5 h-5 text-black" />
                </div>
                <div>
                  <h3 className="font-bold text-black">Laura</h3>
                  <p className="text-xs text-black/70">Online - Ask me about SlotFleet!</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={handleClose}
                  className="p-1.5 hover:bg-black/10 rounded-full transition-colors"
                  aria-label="Minimize"
                >
                  <Minimize2 className="w-4 h-4 text-black" />
                </button>
                <button 
                  onClick={handleClose}
                  className="p-1.5 hover:bg-black/10 rounded-full transition-colors"
                  aria-label="Close"
                >
                  <X className="w-4 h-4 text-black" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-8 scrollbar-thin scrollbar-thumb-amber-500/30 scrollbar-track-transparent">
              {messages.map((msg, index) => (
                <motion.div
                  key={`msg-${msg.role}-${index}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex items-start gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    msg.role === 'user' 
                      ? 'bg-amber-500/20' 
                      : 'bg-gradient-to-r from-amber-500 to-yellow-500'
                  }`}>
                    {msg.role === 'user' 
                      ? <User className="w-4 h-4 text-amber-400" />
                      : <Bot className="w-4 h-4 text-black" />
                    }
                  </div>
                  <div className={`max-w-[80%] p-4 rounded-2xl ${
                    msg.role === 'user'
                      ? 'bg-amber-500 text-black rounded-tr-md'
                      : 'bg-neutral-800 text-white rounded-tl-md'
                  }`}>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </motion.div>
              ))}
              
              {/* Loading indicator */}
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-start gap-2"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-black" />
                  </div>
                  <div className="bg-neutral-800 p-3 rounded-2xl rounded-tl-md">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </motion.div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Quick actions - show only at start */}
            {messages.length <= 1 && !isLoading && (
              <div className="px-4 pb-2 flex flex-wrap gap-2">
                {quickActions.map((action) => (
                  <button
                    key={action.label}
                    onClick={() => {
                      setInputValue(action.message);
                      setTimeout(handleSendMessage, 100);
                    }}
                    className="px-3 py-1.5 text-xs bg-amber-500/10 text-amber-400 rounded-full border border-amber-500/20 hover:bg-amber-500/20 transition-colors"
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            )}

            {/* Input area */}
            <div className="p-4 border-t border-amber-500/10">
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  className="flex-1 bg-neutral-800 border border-amber-500/20 rounded-full px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-500/50"
                  disabled={isLoading}
                  data-testid="chat-input"
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim() || isLoading}
                  className="w-10 h-10 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 p-0 flex items-center justify-center disabled:opacity-50"
                  data-testid="chat-send"
                  aria-label="Send message"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 text-black animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 text-black" />
                  )}
                </Button>
              </div>
              <p className="text-[10px] text-gray-300 text-center mt-2">
                Powered by Laura
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
